import * as React from 'react';
import { Check, ChevronsUpDown, Plus, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Option } from '@/lib/format';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';

export interface MultiSelectProps {
  value: string[];
  onChange: (value: string[]) => void;
  options: ReadonlyArray<Option>;
  placeholder?: string;
  /** Let the user add a value that is not in the list (e.g. a new language). */
  allowCustom?: boolean;
  disabled?: boolean;
  id?: string;
  className?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
}

/**
 * Chips + popover checklist. The shared kit has no multi-select, so positions keep a local one
 * (used for languages in the create form).
 */
export const MultiSelect = React.forwardRef<HTMLButtonElement, MultiSelectProps>(function MultiSelect(
  {
    value,
    onChange,
    options,
    placeholder = 'Select…',
    allowCustom = false,
    disabled,
    id,
    className,
    ...aria
  },
  ref,
) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const selected = new Set(value);
  const toggle = (v: string) => {
    const next = new Set(selected);
    if (next.has(v)) next.delete(v);
    else next.add(v);
    onChange(Array.from(next));
  };
  const labelOf = (v: string) => options.find((o) => o.value === v)?.label ?? v;
  const custom = search.trim();
  const canAddCustom =
    allowCustom &&
    custom.length > 0 &&
    !options.some((o) => o.value.toLowerCase() === custom.toLowerCase()) &&
    !selected.has(custom);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          ref={ref}
          id={id}
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-label={aria['aria-label']}
          aria-labelledby={aria['aria-labelledby']}
          aria-invalid={aria['aria-invalid']}
          aria-describedby={aria['aria-describedby']}
          disabled={disabled}
          className={cn(
            'flex min-h-9 w-full items-center justify-between gap-2 rounded-md border border-input bg-background px-3 py-1.5 text-left text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 aria-[invalid=true]:border-destructive',
            className,
          )}
        >
          <span className="flex min-w-0 flex-1 flex-wrap gap-1">
            {value.length === 0 ? (
              <span className="text-muted-foreground">{placeholder}</span>
            ) : (
              value.map((v) => (
                <Badge key={v} variant="secondary" className="gap-1 pr-1 font-normal">
                  {labelOf(v)}
                  <span
                    role="button"
                    tabIndex={-1}
                    aria-label={`Remove ${labelOf(v)}`}
                    className="rounded-sm p-0.5 hover:bg-background/60"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggle(v);
                    }}
                  >
                    <X className="h-3 w-3" aria-hidden="true" />
                  </span>
                </Badge>
              ))
            )}
          </span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] min-w-56 p-0" align="start">
        <Command>
          <CommandInput placeholder="Search…" value={search} onValueChange={setSearch} />
          <CommandList>
            <CommandEmpty>{canAddCustom ? 'No match.' : 'No options.'}</CommandEmpty>
            {canAddCustom ? (
              <CommandGroup>
                <CommandItem
                  value={`__add__${custom}`}
                  onSelect={() => {
                    toggle(custom);
                    setSearch('');
                  }}
                >
                  <Plus className="h-4 w-4" aria-hidden="true" /> Add “{custom}”
                </CommandItem>
              </CommandGroup>
            ) : null}
            <CommandGroup>
              {options.map((o) => {
                const isSelected = selected.has(o.value);
                return (
                  <CommandItem
                    key={o.value}
                    value={o.label}
                    onSelect={() => toggle(o.value)}
                    role="option"
                    aria-selected={isSelected}
                  >
                    <span
                      className={cn(
                        'flex h-4 w-4 items-center justify-center rounded-sm border border-primary',
                        isSelected ? 'bg-primary text-primary-foreground' : 'opacity-50 [&_svg]:invisible',
                      )}
                      aria-hidden="true"
                    >
                      <Check className="h-3 w-3" />
                    </span>
                    {o.label}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
});
